import { FC, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Icon, ActivityIndicator } from '@kontentino/ui';
import { faSparkles } from '@fortawesome/pro-regular-svg-icons/faSparkles';
import PostApi from 'api/post';
import { queryKey } from 'constants/queryKey';
import { useToast } from 'app/hooks/useToast';
import { ApiClientError } from 'api/client';
import { addRecentLanguage } from 'app/modules/posts/modules/translation/utils/recentLanguages';
import { AI_GENERATE_BUTTON_STYLE_CLASSNAME } from 'app/modules/aiContent/components/AIGenerateButton';
import TranslationResults from './TranslationResults';
import LanguageSelectorPopover from './LanguageSelectorPopover';
import { useTranslationAppliedState } from 'app/modules/posts/hooks/useTranslationAppliedState';
import { useApplyTranslation } from 'app/modules/posts/hooks/useApplyTranslation';
import { useRevertTranslation } from 'app/modules/posts/hooks/useRevertTranslation';
import type { TranslationImageField } from 'types/AiHubTranslation';
import { optimisticallySetTranslationJobProcessing } from './optimisticTranslationUpdate';
import Logger from 'utils/logger';
import { useUserPermissions } from 'app/hooks/useUserPermissions';
import useSubscriptionInfo from 'app/hooks/useSubscriptionInfo';
import { sanitizeTranslationError } from 'app/modules/posts/modules/translation/utils/sanitizeTranslationError';
import { isTranslatedImageReady } from 'app/modules/posts/utils/translatedImageCards';
import { UserPermission } from 'constants/userPermission';
import useInterval from 'app/hooks/useInterval';
import {
  TRANSLATION_LOADING_PHRASES,
  TRANSLATION_LOADING_PHRASES_DEFAULTS,
} from 'app/modules/posts/modules/translation/constants/translationLoadingPhrases';

const POLL_INTERVAL_MS = 3000;

type Props = {
  postId: number;
  pageId?: number;
  hasImages?: boolean;
  hasText?: boolean;
  hasFirstComment?: boolean;
  isLocked?: boolean;
  pageType?: number;
  brandProfileId?: number;
  onAfterApply?: () => void | Promise<void>;
};

const ImageTranslationPanel: FC<Props> = ({
  postId,
  pageId,
  hasImages = false,
  hasText = false,
  hasFirstComment = false,
  isLocked = false,
  pageType,
  brandProfileId,
  onAfterApply,
}) => {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { hasSome } = useUserPermissions();
  const canEditText = hasSome(UserPermission.POST_EDIT_TEXT);
  const { accountPlanType } = useSubscriptionInfo();
  const canEditAssets = hasSome(UserPermission.POST_EDIT_ASSETS);
  const canTriggerTranslation = canEditText && canEditAssets;
  const [isLanguageOpen, setIsLanguageOpen] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const [isJobStarted, setIsJobStarted] = useState(false);
  const [selectedBrandProfileId, setSelectedBrandProfileId] = useState<
    number | null
  >(brandProfileId ?? null);
  const prevTranslationStatusRef = useRef<string | undefined>();

  const jobQuery = useQuery(
    queryKey.postTranslation(postId),
    async () => {
      const data = await PostApi.getTranslationJobByPostId(postId);
      if (
        isJobStarted &&
        data?.status !== 'pending' &&
        data?.status !== 'processing'
      ) {
        setIsJobStarted(false);
      }
      return data;
    },
    {
      refetchInterval: (data) => {
        if (
          isJobStarted ||
          data?.status === 'pending' ||
          data?.status === 'processing'
        ) {
          return POLL_INTERVAL_MS;
        }
        return false;
      },
      onSuccess: (data) => {
        prevTranslationStatusRef.current = data?.status;
      },
    },
  );

  const job = jobQuery.data;

  const createJobMutation = useMutation(
    (params: { targetLanguage: string }) =>
      PostApi.createTranslationJob({
        localPostId: postId,
        targetLanguage: params.targetLanguage,
        translateText: hasText,
        translateFirstComment: hasFirstComment,
        translateImages: hasImages,
        force: !!job,
        brandProfileId: selectedBrandProfileId ?? undefined,
        planType: accountPlanType,
      }),
    {
      onMutate: (params) => {
        optimisticallySetTranslationJobProcessing(
          queryClient,
          postId,
          params.targetLanguage,
        );
        prevTranslationStatusRef.current = 'processing';
      },
      onSuccess: () => {
        setIsLanguageOpen(false);
        setIsDismissed(false);
        setIsJobStarted(true);
        queryClient.invalidateQueries(queryKey.postTranslation(postId));
      },
      onError: (e: ApiClientError) => {
        toast(
          e?.userMessage ||
            t('translation.jobCreationFailed', {
              defaultValue: 'Failed to start translation',
            }),
          'error',
        );
      },
    },
  );

  const retryFailedImagesMutation = useMutation(
    () => PostApi.retryFailedImages(postId),
    {
      onMutate: () => {
        optimisticallySetTranslationJobProcessing(
          queryClient,
          postId,
          job?.targetLanguage ?? '',
        );
      },
      onSuccess: () => {
        setIsJobStarted(true);
        queryClient.invalidateQueries(queryKey.postTranslation(postId));
      },
      onError: (e: ApiClientError) => {
        queryClient.invalidateQueries(queryKey.postTranslation(postId));
        toast(
          e?.userMessage ||
            t('translation.retryFailedImagesFailed', {
              defaultValue: 'Failed to retry image translation',
            }),
          'error',
        );
      },
    },
  );

  const { applyPostText, applyFirstComment, applyImages, applyAll } =
    useApplyTranslation(postId, { onApplied: onAfterApply });
  const { revertPostText, revertFirstComment, revertImages, revertAll } =
    useRevertTranslation(postId, { onReverted: onAfterApply });
  const appliedFields = useTranslationAppliedState(postId, job);

  const applyPostTextField = () => {
    if (job?.fields?.postText?.translated) {
      applyPostText.mutate(job.fields.postText.translated);
    }
  };

  const applyFirstCommentField = () => {
    if (job?.fields?.firstComment?.translated) {
      applyFirstComment.mutate(job.fields.firstComment.translated);
    }
  };

  const enrichImagesWithCardIndex = async (images: TranslationImageField[]) => {
    if (!images?.length) return [];

    const post = await PostApi.detail(postId);
    const cards = post.attachments?.cards ?? [];

    return images.map((img) => ({
      ...img,
      cardIndex: img.cardIndex,
      mediumId:
        img.mediumId ??
        (img.cardIndex != null
          ? cards[img.cardIndex]?.medium?.id || undefined
          : undefined),
    }));
  };

  const applyImagesField = async () => {
    if (!job?.fields?.images) return;

    const enriched = await enrichImagesWithCardIndex(job.fields.images);
    const successfulImages = enriched.filter(isTranslatedImageReady);

    if (successfulImages.length > 0) {
      applyImages.mutate(successfulImages);
    } else {
      const missingMediumId = enriched.filter(
        (img) =>
          img.status === 'completed' &&
          img.translatedUrl &&
          img.cardIndex != null &&
          !isTranslatedImageReady(img),
      );
      const missingCardIndex = enriched.filter(
        (img) =>
          img.status === 'completed' &&
          img.translatedUrl &&
          img.cardIndex == null,
      );

      Logger.warn('[ImageTranslationPanel] Cannot apply images', {
        total: enriched.length,
        missingMediumId: missingMediumId.length,
        missingCardIndex: missingCardIndex.length,
        images: enriched.map((img) => ({
          status: img.status,
          hasTranslatedUrl: !!img.translatedUrl,
          translatedMediumId: img.translatedMediumId,
          translatedAssetId: img.translatedAssetId,
          cardIndex: img.cardIndex,
        })),
      });

      if (missingMediumId.length > 0) {
        toast(
          t('translation.imageMediumMissing', {
            defaultValue:
              'Image translation is incomplete. Please retranslate to apply images.',
          }),
          'warning',
        );
      } else {
        toast(
          t('translation.retranslateRequired', {
            defaultValue:
              'Please retranslate to apply images. The current translation is outdated.',
          }),
          'warning',
        );
      }
    }
  };

  const handleApply = (field: 'postText' | 'firstComment' | 'images') => {
    if (field === 'images' && !canEditAssets) return;
    if ((field === 'postText' || field === 'firstComment') && !canEditText)
      return;

    const handlers = {
      postText: applyPostTextField,
      firstComment: applyFirstCommentField,
      images: applyImagesField,
    };
    handlers[field]();
  };

  const revertPostTextField = () => {
    if (job?.fields?.postText?.original) {
      revertPostText.mutate(job.fields.postText.original);
    }
  };

  const revertFirstCommentField = () => {
    if (job?.fields?.firstComment?.original) {
      revertFirstComment.mutate(job.fields.firstComment.original);
    }
  };

  const revertImagesField = async () => {
    const images = job?.fields?.images ?? [];
    const enriched = await enrichImagesWithCardIndex(images);

    if (enriched.some((img) => img.cardIndex != null)) {
      revertImages.mutate(enriched);
    }
  };

  const handleRevert = (field: 'postText' | 'firstComment' | 'images') => {
    if (field === 'images' && !canEditAssets) return;
    if ((field === 'postText' || field === 'firstComment') && !canEditText)
      return;

    const handlers = {
      postText: revertPostTextField,
      firstComment: revertFirstCommentField,
      images: revertImagesField,
    };
    handlers[field]();
  };

  const handleApplyAll = () => {
    if (!job?.fields) return;

    const permittedFields = {
      ...job.fields,
      postText: canEditText ? job.fields.postText : undefined,
      firstComment: canEditText ? job.fields.firstComment : undefined,
      images: canEditAssets ? job.fields.images : undefined,
    };

    applyAll.mutate(permittedFields);
  };

  const handleRevertAll = () => {
    if (!job?.fields) return;

    const permittedFields = {
      ...job.fields,
      postText: canEditText ? job.fields.postText : undefined,
      firstComment: canEditText ? job.fields.firstComment : undefined,
      images: canEditAssets ? job.fields.images : undefined,
    };

    revertAll.mutate({ fields: permittedFields, appliedFields });
  };

  const handleLanguageSelect = (langValue: string | null) => {
    if (!langValue) return;

    addRecentLanguage(langValue);
    createJobMutation.mutate({ targetLanguage: langValue });
  };

  const hasAnyContent = hasImages || hasText || hasFirstComment;
  const hasActiveJob = job?.status && job.status !== 'pending';

  if (!canTriggerTranslation && !hasActiveJob) {
    return null;
  }

  if (!hasAnyContent && !hasActiveJob && !jobQuery.isLoading) {
    return (
      <div className="tw-flex tw-items-start tw-gap-3 tw-rounded-lg tw-border tw-border-grayscale-20 tw-bg-white tw-p-3 tw-opacity-50">
        <Icon icon={faSparkles} className="tw-mt-1 tw-text-grayscale-100" />
        <div className="tw-flex tw-flex-1 tw-flex-col tw-gap-2">
          <span className="tw-text-sm tw-text-grayscale-140">
            {t('translation.panelDescription', {
              defaultValue:
                'AI-translate your images and post content to any language.',
            })}
          </span>
          <Button
            variant="primary"
            size="small"
            className="tw-w-fit"
            iconBefore={<Icon icon={faSparkles} />}
            disabled
            data-cy={`translation-panel-trigger-disabled-${postId}`}
          >
            {t('translation.translateContent', {
              defaultValue: 'Translate content',
            })}
          </Button>
        </div>
      </div>
    );
  }

  if (jobQuery.isLoading) {
    return null;
  }

  const isProcessing =
    isJobStarted || job?.status === 'pending' || job?.status === 'processing';
  const isCompleted = job?.status === 'completed' && !isDismissed;
  const isFailed = job?.status === 'failed' && !isDismissed;

  const applyingFields = {
    postText: applyPostText.isLoading || revertPostText.isLoading,
    firstComment: applyFirstComment.isLoading || revertFirstComment.isLoading,
    images: applyImages.isLoading || revertImages.isLoading,
    all: applyAll.isLoading || revertAll.isLoading,
  };
  const isApplying = Object.values(applyingFields).some(Boolean);

  const translationInfoText = t('translation.chooseLanguageInfo', {
    defaultValue:
      'Choose a language to translate image text, post text and first comment if available.',
  });

  if (isCompleted && job) {
    const retranslateSlot =
      !isLocked && canTriggerTranslation ? (
        <LanguageSelectorPopover
          id={`retranslate-${postId}`}
          isOpen={isLanguageOpen}
          onOpenChange={setIsLanguageOpen}
          selectedLanguage={null}
          onLanguageSelect={handleLanguageSelect}
          showOriginalOption={false}
          isLoading={createJobMutation.isLoading}
          infoText={translationInfoText}
          showBrandProfileSelector
          selectedBrandProfileId={selectedBrandProfileId}
          onBrandProfileChange={setSelectedBrandProfileId}
          trigger={
            <Button
              variant="secondary"
              size="small"
              iconBefore={<Icon icon={faSparkles} />}
              data-cy={`translation-retranslate-${postId}`}
            >
              {t('translation.translateAgain', {
                defaultValue: 'Translate again',
              })}
            </Button>
          }
        />
      ) : undefined;

    return (
      <TranslationResults
        postId={postId}
        translationJob={job}
        onApply={handleApply}
        onRevert={handleRevert}
        onApplyAll={handleApplyAll}
        onRevertAll={handleRevertAll}
        appliedFields={appliedFields}
        isApplying={isApplying}
        applyingFields={applyingFields}
        isLocked={isLocked}
        retranslateSlot={retranslateSlot}
        onRetryFailedImages={() => retryFailedImagesMutation.mutate()}
        isRetryingImages={retryFailedImagesMutation.isLoading}
        canEditText={canEditText}
        canEditAssets={canEditAssets}
        canTriggerTranslation={canTriggerTranslation}
        pageType={pageType}
        page={
          pageId && pageType != null
            ? { id: pageId, type: pageType }
            : undefined
        }
      />
    );
  }

  if (isProcessing || createJobMutation.isLoading) {
    return <TranslationProcessingState />;
  }

  if (isFailed) {
    return (
      <Alert
        variant="warning"
        title={t('translation.translationFailed', {
          defaultValue: 'Translation failed',
        })}
        onClose={() => setIsDismissed(true)}
      >
        <span className="tw-text-sm tw-text-grayscale-180">
          {sanitizeTranslationError(job?.errorMessage) ||
            t('translation.translationFailedDescription', {
              defaultValue:
                'Something went wrong during translation. Please try again.',
            })}
        </span>
      </Alert>
    );
  }

  if (!canTriggerTranslation) {
    return null;
  }

  return (
    <div className="tw-flex tw-items-start tw-gap-3 tw-rounded-lg tw-border tw-border-grayscale-20 tw-bg-white tw-p-3">
      <Icon icon={faSparkles} className="tw-mt-1 tw-text-primary-100" />
      <div className="tw-flex tw-flex-1 tw-flex-col tw-gap-2">
        <span className="tw-text-sm tw-text-grayscale-180">
          {t('translation.panelDescription', {
            defaultValue:
              'AI-translate your images and post content to any language.',
          })}
        </span>
        <LanguageSelectorPopover
          id={`panel-${postId}`}
          isOpen={isLanguageOpen}
          onOpenChange={setIsLanguageOpen}
          selectedLanguage={null}
          onLanguageSelect={handleLanguageSelect}
          showOriginalOption={false}
          isLoading={createJobMutation.isLoading}
          infoText={translationInfoText}
          showBrandProfileSelector
          selectedBrandProfileId={selectedBrandProfileId}
          onBrandProfileChange={setSelectedBrandProfileId}
          trigger={
            <Button
              variant="primary"
              size="small"
              className={`tw-w-fit ${AI_GENERATE_BUTTON_STYLE_CLASSNAME}`}
              iconBefore={<Icon icon={faSparkles} />}
              data-cy={`translation-panel-trigger-${postId}`}
            >
              {t('translation.translateContent', {
                defaultValue: 'Translate content',
              })}
            </Button>
          }
        />
      </div>
    </div>
  );
};

const PHRASE_INTERVAL_MS = 3000;

const TranslationProcessingState: FC = () => {
  const { t } = useTranslation();
  const [phraseIndex, setPhraseIndex] = useState(0);

  useInterval(() => {
    setPhraseIndex((prev) => (prev + 1) % TRANSLATION_LOADING_PHRASES.length);
  }, PHRASE_INTERVAL_MS);

  return (
    <div className="tw-flex tw-items-center tw-gap-3 tw-rounded-lg tw-border tw-border-grayscale-20 tw-bg-white tw-p-3">
      <ActivityIndicator className="tw-h-4 tw-w-4 tw-shrink-0 tw-text-primary-100" />
      <div className="tw-relative tw-h-5 tw-flex-1">
        {TRANSLATION_LOADING_PHRASES.map((key, index) => (
          <span
            key={key}
            className={`tw-absolute tw-inset-0 tw-text-sm tw-text-grayscale-140 tw-transition-opacity tw-duration-500 ${
              index === phraseIndex ? 'tw-opacity-100' : 'tw-opacity-0'
            }`}
          >
            {t(key, {
              defaultValue: TRANSLATION_LOADING_PHRASES_DEFAULTS[key] ?? key,
            })}
          </span>
        ))}
      </div>
    </div>
  );
};

export default ImageTranslationPanel;
